import React from 'react';
import ReactDOM from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { msalInstance, installApiFetchInterceptor, purgeMsalStorage, restoreActiveAccount } from './auth/msalConfig';

const API = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:5295';

// Wrap window.fetch once at boot so every API call carries the bearer token
// and active-tenant header without per-page changes.
installApiFetchInterceptor(API);

function render() {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <ErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </ErrorBoundary>
  );
}

// MSAL must finish handling any redirect response before React mounts; if we
// render too early the redirect handshake races against the first render and
// the user appears unauthenticated for a flicker.
//
// CRITICAL boot invariants (each prevents a class of permanent white page):
//   1. Render in `finally`, never only on success — a *rejected*
//      handleRedirectPromise() (stale interaction state after inactivity) must
//      not leave <div id="root"> empty forever.
//   2. Race initialize()/handleRedirectPromise() against a timeout — a *hung*
//      (never-settling) promise, e.g. cache migration choking on a corrupt
//      localStorage entry, would skip even the `finally`. The timeout guarantees
//      we always reach render().
//   3. On ANY failure or timeout, purge the FULL MSAL cache (both web stores),
//      not just `msal.`-prefixed keys, so a corrupt credential entry can't
//      re-poison the next load. Worst case the user lands on the sign-in screen.
const BOOT_TIMEOUT_MS = 8000;

function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('msal-boot-timeout')), ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

// One-shot guard so the recovery reload below can never loop. Survives the
// purge (it's not an MSAL key) and is cleared once boot succeeds.
const RECOVERY_FLAG = 'saara_boot_recovery';

async function boot() {
  try {
    await withTimeout(msalInstance.initialize(), BOOT_TIMEOUT_MS);
    await withTimeout(msalInstance.handleRedirectPromise(), BOOT_TIMEOUT_MS);
    restoreActiveAccount();
    try { sessionStorage.removeItem(RECOVERY_FLAG); } catch { /* ignore */ }
    render();
  } catch (e) {
    console.error('MSAL bootstrap failed; purging cache', e);
    purgeMsalStorage();

    // A hung initialize()/handleRedirectPromise() promise is memoized in memory,
    // so MsalProvider would await the same stuck promise and never leave
    // "Signing you in…". Reload ONCE to get a fresh instance over the now-clean
    // cache. If we've already tried that this session, stop and render so we
    // never loop — the user lands on the sign-in screen instead of a white page.
    let alreadyRecovered = false;
    try { alreadyRecovered = sessionStorage.getItem(RECOVERY_FLAG) === '1'; } catch { /* ignore */ }

    if (!alreadyRecovered) {
      try { sessionStorage.setItem(RECOVERY_FLAG, '1'); } catch { /* ignore */ }
      window.location.reload();
      return;
    }
    render();
  }
}

boot();
